<div class="labour-report-container">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <h2 class="page-title">
        <i class="material-icons">assignment</i>
        {{ isEditMode ? 'Edit' : 'Add New' }} Daily Labour Report
      </h2>
      <div class="header-actions">
        <button type="button" class="btn-icon" (click)="showCategoryManager = !showCategoryManager" matTooltip="Manage Categories">
          <mat-icon>settings</mat-icon>
        </button>
        <button type="button" class="btn-icon" (click)="showColumnConfig = !showColumnConfig" matTooltip="Configure Columns">
          <mat-icon>view_column</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Category Manager Panel -->
  <div class="column-config-panel" *ngIf="showCategoryManager">
    <div class="config-header">
      <h6>Manage Labour Categories</h6>
      <button type="button" class="btn-close" (click)="showCategoryManager = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="config-content">
      <!-- Add New Category -->
      <div class="add-category-section">
        <h6 class="section-title">Add New Category</h6>
        <div class="add-category-input">
          <input type="text" class="form-control-modern" [(ngModel)]="newCategoryLabel" 
                 placeholder="Enter category name (e.g., Electrician)" 
                 (keyup.enter)="addCategory()">
          <button type="button" class="btn-add-category" (click)="addCategory()">
            <mat-icon>add</mat-icon>
            Add
          </button>
        </div>
      </div>

      <!-- Categories List -->
      <div class="categories-list-section">
        <h6 class="section-title">Categories</h6>
        <div class="categories-list">
          <div *ngFor="let cat of labourCategories" class="category-item">
            <div *ngIf="editingCategoryKey !== cat.key" class="category-display">
              <div class="category-info">
                <span class="category-label">{{ cat.label }}</span>
                <span class="category-key">({{ cat.key }})</span>
              </div>
              <div class="category-actions">
                <button type="button" class="btn-edit-category" (click)="startEditCategory(cat.key)" matTooltip="Edit">
                  <mat-icon>edit</mat-icon>
                </button>
                <button type="button" class="btn-remove-category" (click)="removeCategory(cat.key)" 
                        [disabled]="labourCategories.length <= 1" matTooltip="Remove">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>
            <div *ngIf="editingCategoryKey === cat.key" class="category-edit">
              <input type="text" class="form-control-modern" [(ngModel)]="editingCategoryLabel">
              <div class="edit-actions">
                <button type="button" class="btn-save-category" (click)="saveCategoryEdit()">
                  <mat-icon>check</mat-icon>
                </button>
                <button type="button" class="btn-cancel-category" (click)="cancelCategoryEdit()">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Column Configuration Panel -->
  <div class="column-config-panel" *ngIf="showColumnConfig">
    <div class="config-header">
      <h6>Configure Columns Visibility</h6>
      <button type="button" class="btn-close" (click)="showColumnConfig = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="config-content">
      <div class="checkbox-grid">
        <label *ngFor="let cat of labourCategories" class="checkbox-item">
          <input type="checkbox" [checked]="cat.visible" (change)="toggleColumnVisibility(cat.key)">
          <span>{{ cat.label }}</span>
        </label>
      </div>
    </div>
  </div>

  <form [formGroup]="reportForm" (ngSubmit)="onSubmit()">
    <!-- Basic Information Card -->
    <div class="info-card">
      <div class="card-header-modern">
        <h5 class="card-title">
          <mat-icon>info</mat-icon>
          Basic Information
        </h5>
      </div>
      <div class="card-body-modern">
        <div class="form-grid">
          <div class="form-field">
            <label class="form-label">
              Project <span class="required">*</span>
            </label>
            <select class="form-control-modern" formControlName="project_id" 
                    [class.is-invalid]="reportForm.get('project_id')?.invalid && reportForm.get('project_id')?.touched">
              <option value="">Select Project</option>
              <option *ngFor="let project of projects" [value]="project.id">
                {{ project.project_name }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label class="form-label">
              Report Date <span class="required">*</span>
            </label>
            <input type="date" class="form-control-modern" formControlName="report_date" 
                   [class.is-invalid]="reportForm.get('report_date')?.invalid && reportForm.get('report_date')?.touched">
          </div>

          <div class="form-field">
            <label class="form-label">
              Shift Type <span class="required">*</span>
            </label>
            <select class="form-control-modern" formControlName="shift_type" 
                    [class.is-invalid]="reportForm.get('shift_type')?.invalid && reportForm.get('shift_type')?.touched">
              <option *ngFor="let shift of shiftTypes" [value]="shift.value">
                {{ shift.label }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label class="form-label">Contractor Name</label>
            <input type="text" class="form-control-modern" formControlName="contractor_name" placeholder="Enter contractor name">
          </div>

          <div class="form-field">
            <label class="form-label">Client Name</label>
            <input type="text" class="form-control-modern" formControlName="client_name" placeholder="Enter client name">
          </div>

          <div class="form-field full-width">
            <label class="form-label">Hindrance</label>
            <textarea class="form-control-modern" formControlName="hindrance" rows="3" 
                      placeholder="Enter any hindrances or issues faced..."></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Labour Details Card -->
    <div class="info-card">
      <div class="card-header-modern">
        <h5 class="card-title">
          <mat-icon>group</mat-icon>
          Labour Strength Details
        </h5>
        <button type="button" class="btn-primary-icon" (click)="addDetailRow()">
          <mat-icon>add</mat-icon>
          <span>Add Row</span>
        </button>
      </div>
      <div class="card-body-modern">
        <div class="table-wrapper">
          <table class="labour-table">
            <thead>
              <tr>
                <th class="col-subcontractor">Name of Sub-Contractor</th>
                <th *ngFor="let cat of getVisibleColumns()" class="col-number">{{ cat.label }}</th>
                <th class="col-total">Total</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody formArrayName="details">
              <tr *ngFor="let detail of detailsArray.controls; let i = index" [formGroupName]="i" class="table-row">
                <td class="col-subcontractor">
                  <input type="text" class="form-control-table" formControlName="sub_contractor_name" 
                         placeholder="Sub-contractor name">
                </td>
                <td *ngFor="let cat of getVisibleColumns()" class="col-number">
                  <input type="number" class="form-control-table number-input" 
                         [formControlName]="cat.key" min="0" (blur)="calculateRowTotal(i)">
                </td>
                <td class="col-total total-cell">
                  <span class="total-value">{{ calculateRowTotal(i) }}</span>
                </td>
                <td class="col-actions">
                  <div class="action-buttons">
                    <button type="button" class="btn-action btn-copy" (click)="copyDetailRow(i)" matTooltip="Copy Row">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                    <button type="button" class="btn-action btn-delete" (click)="removeDetailRow(i)" matTooltip="Delete Row">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </td>
              </tr>
              <tr *ngIf="detailsArray.length === 0" class="empty-row">
                <td [attr.colspan]="getVisibleColumns().length + 3" class="text-center text-muted">
                  <div class="empty-state">
                    <mat-icon>inbox</mat-icon>
                    <p>No labour details added. Click "Add Row" to add details.</p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot *ngIf="detailsArray.length > 0">
              <tr class="subtotal-row">
                <td class="subtotal-label">Sub Total</td>
                <td *ngFor="let cat of getVisibleColumns()" class="subtotal-value">
                  {{ getSubTotal(cat.key) }}
                </td>
                <td class="subtotal-total">{{ calculateGrandTotal() }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-footer">
      <button type="button" class="btn-secondary" (click)="cancel()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button type="submit" class="btn-primary" [disabled]="loading || reportForm.invalid">
        <mat-spinner *ngIf="loading" diameter="20" class="spinner-inline"></mat-spinner>
        <mat-icon *ngIf="!loading">{{ isEditMode ? 'update' : 'save' }}</mat-icon>
        {{ isEditMode ? 'Update' : 'Create' }} Report
      </button>
    </div>
  </form>
</div>
